import React, { useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import { IconButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import LinkFilledIconOld from '@atlaskit/icon/glyph/link-filled';
import type { IconProps } from '@atlaskit/icon/types';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import Flyout from './flyout';
import messages from './messages';
import type { Props } from './types';

const DependencyIndicatorIcon = (props: IconProps) => {
	const { formatMessage } = useIntl();
	const secondaryColor = token('color.text', colors.N700);
	const primaryColor = token('color.background.neutral', colors.N40);
	const message = formatMessage(messages.viewDependencyDetails);
	return (
		<LinkIcon
			{...props}
			label={message}
			color={token('color.icon', colors.N500)}
			LEGACY_fallbackIcon={LinkFilledIconOld}
			LEGACY_size="medium"
			LEGACY_primaryColor={primaryColor}
			LEGACY_secondaryColor={secondaryColor}
		/>
	);
};

export const IssueLinksIndicator = (props: Props) => {
	const { formatMessage } = useIntl();
	const [popupOpen, setPopupOpen] = useState(false);

	const flyoutContent = useCallback(
		() => <Flyout issueKey={props.issueKey} issueId={props.issueId} />,
		[props.issueKey, props.issueId],
	);

	const onIndicatorBtnClick = useCallback(() => {
		setPopupOpen(!popupOpen);
	}, [popupOpen, setPopupOpen]);

	const onPopUpClose = useCallback(() => {
		setPopupOpen(false);
	}, [setPopupOpen]);

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			const message = formatMessage(messages.viewDependencyDetails);

			return (
				<Tooltip content={message}>
					<IconButton
						{...triggerProps}
						spacing="compact"
						appearance="subtle"
						shape="circle"
						onClick={onIndicatorBtnClick}
						isSelected={popupOpen}
						label={message}
						icon={DependencyIndicatorIcon}
						testId="software-board.board-container.board.card-container.card.issue-links-indicator.icon-button"
					/>
				</Tooltip>
			);
		},
		[onIndicatorBtnClick, popupOpen, formatMessage],
	);
	return (
		<Container>
			<Popup
				isOpen={popupOpen}
				content={flyoutContent}
				trigger={trigger}
				onClose={onPopUpClose}
				shouldUseCaptureOnOutsideClick
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	lineHeight: 1,
});

export default IssueLinksIndicator;
